import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Button,
  message,
  Typography,
} from 'antd';
import { ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { makeGetCall } from '../../utils/helper';
import moment from 'moment';

const { Option } = Select;

interface EnrolledUsersListProps {
  // When `program` is passed, the list is scoped to that program: no program
  // dropdown, no program column, header reflects the program. This is how the
  // page is consumed under Program Management → row click. The unscoped form
  // is kept for backwards compatibility.
  program?: any;
  onBack?: () => void;
}

const EnrolledUsersList: React.FC<EnrolledUsersListProps> = ({ program, onBack }) => {
  const scoped = !!program;
  const [users, setUsers] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [programFilter, setProgramFilter] = useState<number | undefined>(
    program?.program_id
  );
  const [search, setSearch] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const effectiveProgramId = scoped ? program.program_id : programFilter;
      if (effectiveProgramId !== undefined) {
        params.append('programId', String(effectiveProgramId));
      }
      if (search.trim()) params.append('search', search.trim());
      const qs = params.toString();
      const response = await makeGetCall(`/enrolled-user/list${qs ? `?${qs}` : ''}`);
      setUsers(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch enrolled users');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchPrograms = async () => {
    try {
      const response = await makeGetCall('/program/list');
      setPrograms(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch programs', error);
    }
  };

  useEffect(() => {
    if (!scoped) {
      fetchPrograms();
    }
  }, [scoped]);

  // Debounce search; refetch immediately on program filter change
  useEffect(() => {
    const t = setTimeout(fetchUsers, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, programFilter, program?.program_id]);

  const programById = useMemo(() => {
    const map = new Map<number, any>();
    programs.forEach((p) => map.set(p.program_id, p));
    return map;
  }, [programs]);

  const renderProgramCell = (record: any) => {
    if (!record.enrolled_user_program_id) {
      return <span style={{ color: '#999' }}>—</span>;
    }
    const fromInclude = record.program;
    const fromList = programById.get(record.enrolled_user_program_id);
    const name =
      fromInclude?.program_name ||
      fromList?.program_name ||
      `Program #${record.enrolled_user_program_id}`;
    const isActive =
      fromInclude?.program_is_active ?? fromList?.program_is_active ?? true;
    return (
      <Space size={6}>
        <span>{name}</span>
        {!isActive && <Tag color="default">Inactive</Tag>}
      </Space>
    );
  };

  const columns: any = [
    {
      title: 'ID',
      dataIndex: 'enrolled_user_id',
      key: 'enrolled_user_id',
      width: 80,
      render: (id: number) => (
        <Typography.Text strong style={{ fontFamily: 'monospace' }}>
          {id}
        </Typography.Text>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'enrolled_user_email',
      key: 'enrolled_user_email',
      width: '25%',
      render: (email: string) => (
        <Typography.Text copyable={{ text: email }}>{email}</Typography.Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'enrolled_user_name',
      key: 'enrolled_user_name',
      width: '20%',
      render: (name: string) =>
        name || <span style={{ color: '#999' }}>(unnamed)</span>,
    },
    // Program column is only useful in the global (unscoped) view.
    ...(!scoped ? [{
      title: 'Current Program',
      key: 'program',
      width: '25%',
      render: (_: any, record: any) => renderProgramCell(record),
    }] : []),
    {
      title: 'Last Login',
      dataIndex: 'enrolled_user_last_login',
      key: 'enrolled_user_last_login',
      width: 170,
      render: (val: string) =>
        val ? (
          <Tooltip title={moment(val).format('DD MMM YYYY, HH:mm:ss')}>
            {moment(val).fromNow()}
          </Tooltip>
        ) : (
          <span style={{ color: '#999' }}>Never</span>
        ),
    },
    {
      title: 'Enrolled On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (val: string) =>
        val ? moment(val).format('DD MMM YYYY') : <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Source',
      dataIndex: 'enrolled_user_created_by',
      key: 'enrolled_user_created_by',
      width: 160,
      render: (val: string) =>
        val || <span style={{ color: '#999' }}>—</span>,
    },
  ];

  const heading = scoped
    ? (program.program_name || `Program #${program.program_id}`)
    : 'Enrolled Users';
  const subheading = scoped
    ? `Users enrolled in this program via the program-enroller integration. Read-only — enrollment is driven by the external service.`
    : `Users provisioned by the program-enroller integration. Read-only — enrollment is driven by the external service.`;

  return (
    <div className="p-4">
      {scoped && onBack && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          className="mb-2"
        >
          Back to Programs
        </Button>
      )}
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">
            {scoped ? `${heading} — Enrolled Users` : heading}
          </h2>
          <p className="text-gray-500">{subheading}</p>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchUsers}
        >
          Refresh
        </Button>
      </div>

      <div className="mb-3 flex gap-2 items-center">
        <Input.Search
          allowClear
          placeholder="Search by email or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 320 }}
        />
        {!scoped && (
          <Select
            allowClear
            placeholder="Filter by program"
            value={programFilter}
            onChange={(v) => setProgramFilter(v)}
            style={{ width: 280 }}
            showSearch
            optionFilterProp="children"
          >
            {programs.map((p) => (
              <Option key={p.program_id} value={p.program_id}>
                {p.program_name || `Program #${p.program_id}`}
              </Option>
            ))}
          </Select>
        )}
        <span className="text-gray-500" style={{ marginLeft: 'auto' }}>
          {users.length} user{users.length === 1 ? '' : 's'}
        </span>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="enrolled_user_id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: ['bottomCenter'],
        }}
        scroll={{ x: 1100 }}
      />
    </div>
  );
};

export default EnrolledUsersList;
